/**
 * useAdvisorLiveSync — VÉRAULT advisor cockpit live-sync (Wave 30.3 · Phase 9d)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Polls `/api/admin/ecosystem-events?since={iso}` every 30 seconds
 * (visibility-aware) to surface calm toasts when the CLIENT takes an
 * action that needs the advisor's awareness — signatures, uploads,
 * approvals, revisions, new messages, travel requests.
 *
 * The same mental model as `useLiveSync` on the client side: one hook,
 * mounted high in the advisor shell, dispatches window events any
 * advisor panel can subscribe to. Never noisy — calm + de-duplicated.
 *
 * Toast taxonomy (premium, restrained):
 *   agreement_signed:        "Agreement signed."           · 〈client name〉
 *   document_uploaded:       "A document arrived."         · 〈client name〉
 *   form_submitted:          "A form was submitted."       · 〈client name〉
 *   approval_state_changed:  "Approval status updated."    · 〈trip · state〉
 *   message_sent:            "A new message arrived."      · From 〈client〉
 *   travel_request_created:  "A new travel wish arrived."  · 〈client name〉
 *   memory_captured:         (silent — private to the client)
 *   conflict_detected:       (silent — handled by the 409 interceptor)
 *
 * Window events: `vt:advisor-sync:{kind}` and `vt:advisor-sync:any`.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const POLL_INTERVAL_MS = 30000;
const TOAST_DEDUP_MS = 45000;

// Quiet by default — only client-initiated events surface toasts.
const TOAST_COPY = {
  agreement_signed: {
    title: "Agreement signed.",
    description: (e) => e.actor_name || "A client moved a step forward.",
  },
  document_uploaded: {
    title: "A document arrived.",
    description: (e) => e.actor_name ? `From ${e.actor_name}.` : "A client uploaded a file.",
  },
  form_submitted: {
    title: "A form was submitted.",
    description: (e) => e.actor_name || "A traveler completed their intake.",
  },
  approval_state_changed: {
    title: "Approval status updated.",
    description: (e) => {
      const state = e?.payload?.trip_status;
      if (state === "approved") return "The client approved the itinerary.";
      if (state === "changes_requested") return "The client requested revisions.";
      return e.actor_name || "Status quietly shifted.";
    },
  },
  message_sent: {
    title: "A new message arrived.",
    description: (e) => e.actor_name ? `From ${e.actor_name}.` : "A client sent you a note.",
  },
  travel_request_created: {
    title: "A new travel wish arrived.",
    description: (e) => e.actor_name || "A client has sent you something to shape.",
  },
};

const SILENT_KINDS = new Set([
  "memory_captured",
  "conflict_detected",
  "reveal_scheduled",
  "reveal_unlocked",
  // Wave 30.5 · Phase 10 — orchestration events are silent on the
  // advisor cockpit (clients receive their own calm notification).
  "experience_state_changed",
  "deposit_reminder",
  "prep_pulse",
  "final_check",
  "reveal_imminent",
  "balance_reminder",
]);

// Wave 30.5 · Phase 10b — Advisor live-sync also drives the
// orchestrator tick once per minute as a calm side-effect. Keeps the
// scheduler running without a separate cron worker. Tick is idempotent
// + capped at one call per ORCHESTRATOR_TICK_MIN_MS regardless of
// poll cadence.
const ORCHESTRATOR_TICK_MIN_MS = 60000;

/**
 * @param {object} options
 *   - enabled (bool): default true. Pass false to mount but pause polling.
 *   - intervalMs (number): override poll cadence.
 *   - silent (bool): suppress toasts; events still emit to window.
 */
export function useAdvisorLiveSync(options = {}) {
  const { enabled = true, intervalMs = POLL_INTERVAL_MS, silent = false } = options;
  const sinceRef = useRef(null);
  const lastToastAt = useRef({});
  const isFirstRun = useRef(true);
  const lastTickAt = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer = null;
    let visibilityHandler = null;

    const emit = (kind, ev) => {
      window.dispatchEvent(new CustomEvent(`vt:advisor-sync:${kind}`, { detail: ev }));
      window.dispatchEvent(new CustomEvent("vt:advisor-sync:any", { detail: ev }));
    };

    const maybeToast = (kind, ev) => {
      if (silent || SILENT_KINDS.has(kind)) return;
      const copy = TOAST_COPY[kind];
      if (!copy) return;
      const last = lastToastAt.current[kind] || 0;
      const now = Date.now();
      if (now - last < TOAST_DEDUP_MS) return;
      lastToastAt.current[kind] = now;
      const desc = typeof copy.description === "function" ? copy.description(ev) : copy.description;
      toast(copy.title, { description: desc, duration: 4800 });
    };

    const pollOnce = async () => {
      try {
        const params = sinceRef.current ? { since: sinceRef.current, limit: 50 } : { limit: 1 };
        const res = await api.get("/admin/ecosystem-events", { params });
        if (cancelled) return;
        const events = Array.isArray(res.data) ? res.data : [];
        // Stamp the newest timestamp we've seen for the next poll cycle.
        if (events.length) {
          // Events are sorted desc by `created_at` server-side.
          const newest = events[0]?.created_at;
          if (newest) sinceRef.current = newest;
        } else if (!sinceRef.current) {
          sinceRef.current = new Date().toISOString();
        }

        if (!isFirstRun.current) {
          // Process oldest → newest so toasts arrive in real order.
          for (const ev of [...events].reverse()) {
            const kind = ev.kind;
            // Only surface client-initiated actions in toasts.
            if (ev.actor_role === "client") {
              maybeToast(kind, ev);
            }
            emit(kind, ev);
          }
        }
        isFirstRun.current = false;

        // Wave 30.5 · Phase 10b — Run the orchestrator tick at most
        // once per minute as a side-effect of any live-sync poll.
        // Idempotent + cheap; fires due triggers + scans implicits.
        const nowMs = Date.now();
        if (nowMs - lastTickAt.current >= ORCHESTRATOR_TICK_MIN_MS) {
          lastTickAt.current = nowMs;
          try {
            await api.post("/admin/orchestrator/tick");
          } catch {
            /* calm failure — orchestrator is best-effort */
          }
        }
      } catch (err) {
        // Calm failure — never alarm the advisor on a transient poll error.
      }
    };

    const schedule = () => {
      timer = window.setTimeout(async () => {
        if (document.visibilityState === "visible") {
          await pollOnce();
        }
        schedule();
      }, intervalMs);
    };

    // Baseline (silent) poll then begin schedule.
    pollOnce();
    schedule();

    visibilityHandler = () => {
      if (document.visibilityState === "visible") pollOnce();
    };
    document.addEventListener("visibilitychange", visibilityHandler);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      if (visibilityHandler) {
        document.removeEventListener("visibilitychange", visibilityHandler);
      }
    };
  }, [enabled, intervalMs, silent]);
}

export default useAdvisorLiveSync;
